import React, {
  useEffect, useRef, useState, useContext,
} from "react"
import {
  Box,
  FormControl,
  HStack,
  Input,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { PrinterModelLabels } from "components/BadgeBuilder/utils/printers"
import { BadgeTemplate } from "sharedTypes/badge"
import { ToggleButtonGroupItem } from "components/ReactHookForm/ToggleButtonGroup"
import { BadgeUIContext } from "../contexts/BadgeUIContext"
import { getCapability, getContentOrientationRelativeToRollFeed } from "../utils/badgeHelpers"
import { formatMeasurement } from "../utils/units"
import BadgeDimensionsPreview from "./BadgeDimensionsPreview"

type Props = {
  template: BadgeTemplate
  onWidthChange: (width: number) => void
  onHeightChange: (height: number) => void
  onInputKeyDown?: (e: React.KeyboardEvent) => void
  orientation?: "parallel" | "perpendicular"
  onOrientationChange?: (orientation: "parallel" | "perpendicular") => void
  isDisabled?: boolean
}

const BrotherDimensionsForm = ({
  template,
  onWidthChange,
  onHeightChange,
  onInputKeyDown,
  orientation: propOrientation,
  onOrientationChange,
  isDisabled = false,
}: Props) => {
  const capability = getCapability(template.printerModel, template.printerMedia)
  const { printableSize } = capability

  if (printableSize.type !== "roll") {
    throw new Error("BrotherDimensionsForm can only be used with roll media")
  }

  const rollSize = printableSize
  const uiContext = useContext(BadgeUIContext)
  const displayUnit = uiContext?.displayUnit || "mm"

  const { widthMm: width, heightMm: height } = template
  const orientation = propOrientation ?? getContentOrientationRelativeToRollFeed(template)

  const getRollWidth = () => rollSize.widthMm
  const getCutLength = () => (orientation === "parallel" ? width : height)

  const minAllowed = rollSize.minLengthMm
  const maxAllowed = rollSize.maxLengthMm

  const setBadgeDimensions = (newCutLength: number) => {
    const clamped = Math.max(minAllowed, Math.min(maxAllowed, Math.round(newCutLength)))
    if (orientation === "parallel") {
      onWidthChange(clamped)
      onHeightChange(getRollWidth())
    } else {
      onWidthChange(getRollWidth())
      onHeightChange(clamped)
    }
  }

  const formatWithFallback = (valueMm: number) => {
    const formatted = formatMeasurement(valueMm, displayUnit)

    return displayUnit === "mm" ? formatted : `${formatted} (${valueMm} mm)`
  }

  const formatRange = (minMm: number, maxMm: number) => {
    const formattedMin = formatMeasurement(minMm, displayUnit)
    const formattedMax = formatMeasurement(maxMm, displayUnit)

    return displayUnit === "mm"
      ? `${formattedMin}-${formattedMax}`
      : `${formattedMin}-${formattedMax} (${minMm}-${maxMm} mm)`
  }

  const [tempCutLength, setTempCutLength] = useState<string>(getCutLength().toString())
  const [isEditingInput, setIsEditingInput] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isEditingInput) {
      setTempCutLength(getCutLength().toString())
    }
  }, [width, height, orientation, rollSize.widthMm])

  const handleInputTyping = (valueStr: string) => {
    setTempCutLength(valueStr)
  }

  const applyTempToModel = () => {
    const numeric = Number(tempCutLength)
    if (!Number.isFinite(numeric)) {
      setTempCutLength(getCutLength().toString())

      return
    }

    const clamped = Math.max(minAllowed, Math.min(maxAllowed, Math.round(numeric)))
    setBadgeDimensions(clamped)
    setTempCutLength(clamped.toString())
  }

  const handleInputBlur = () => {
    setIsEditingInput(false)
    applyTempToModel()
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      applyTempToModel()
      inputRef.current?.blur()
    } else if (e.key === "Escape") {
      setTempCutLength(getCutLength().toString())
      inputRef.current?.blur()
    }

    if (onInputKeyDown) {
      onInputKeyDown(e)
    }
  }

  return (
    <VStack spacing={6} align="stretch" overflow="hidden">
      <VStack align="stretch">
        <Text as="h2" fontSize="lg" fontWeight="semibold">
          {PrinterModelLabels[template.printerModel]}
        </Text>
        <Text>
          The paper roll for this printer has a fixed roll width of{" "}
          {formatWithFallback(getRollWidth())}.
        </Text>
      </VStack>

      <VStack align="stretch">
        <Text as="h2" fontSize="lg" fontWeight="semibold">
          Orientation
        </Text>
        <FormControl>
          <HStack spacing={3}>
            <ToggleButtonGroupItem
              selected={orientation === "parallel"}
              onClick={() => {
                if (!isDisabled && orientation !== "parallel") {
                  onHeightChange(getRollWidth())
                  onWidthChange(getCutLength())
                  if (onOrientationChange) {
                    onOrientationChange("parallel")
                  }
                }
              }}
              w="120px"
              p={4}
            >
              <VStack spacing={2}>
                <Text fontSize="xl" fontWeight="bold" color="blue.600">
                  A
                </Text>
              </VStack>
            </ToggleButtonGroupItem>

            <ToggleButtonGroupItem
              selected={orientation === "perpendicular"}
              onClick={() => {
                if (!isDisabled && orientation !== "perpendicular") {
                  onWidthChange(getRollWidth())
                  onHeightChange(getCutLength())
                  if (onOrientationChange) {
                    onOrientationChange("perpendicular")
                  }
                }
              }}
              w="120px"
              p={4}
            >
              <VStack spacing={2}>
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  color="blue.600"
                  transform="rotate(-90deg)"
                >
                  A
                </Text>
              </VStack>
            </ToggleButtonGroupItem>
          </HStack>
        </FormControl>
      </VStack>

      <VStack align="stretch">
        <Text as="h2" fontSize="lg" fontWeight="semibold">
          Cut Length
        </Text>

        <Box maxW="300px">
          <HStack spacing={3}>
            <Slider
              value={getCutLength()}
              onChange={(newCutLength) => {
                const clamped = Math.max(minAllowed, Math.min(maxAllowed, Math.round(newCutLength)))
                setBadgeDimensions(clamped)
                setTempCutLength(clamped.toString())
              }}
              min={minAllowed}
              max={maxAllowed}
              step={1}
              flex={1}
              isDisabled={isDisabled}
            >
              <SliderTrack>
                <SliderFilledTrack bg="blue.500" />
              </SliderTrack>
              <SliderThumb />
            </Slider>

            <Input
              ref={inputRef}
              type="text" // use text so we can keep partial values like empty or "-" while typing
              value={tempCutLength}
              onFocus={() => setIsEditingInput(true)}
              onChange={(e) => handleInputTyping(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              inputMode="numeric"
              pattern="[0-9]*"
              min={minAllowed}
              max={maxAllowed}
              w="80px"
              size="sm"
              isDisabled={isDisabled}
            />
          </HStack>

          <Text fontSize="xs" color="gray.600">
            Range: {formatRange(minAllowed, maxAllowed)}
          </Text>
        </Box>
      </VStack>

      <BadgeDimensionsPreview
        rollWidth={getRollWidth()}
        cutLength={getCutLength()}
        orientation={orientation}
      />

      <Box w="full" p={1} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
        <HStack fontWeight="bold" justifyContent="center">
          <Text as="span">Final Badge Size:</Text>
          <Text as="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{formatWithFallback(width)} × {formatWithFallback(height)}</Text>
        </HStack>
      </Box>
    </VStack>
  )
}

export default BrotherDimensionsForm
